import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  AllergyAPI,
  AllergyApiError,
  AllergyCreate,
  AllergyReactionType,
  AllergyResponse,
  AllergySeverity,
} from "../services/allergy.service";
import { db } from "../db/schema";
import { putAndQueue } from "../db/operations";
import { enqueue } from "../services/offline-sync.service";
import { unwrapFromStorage } from "../db/encryption-bridge";
import type { AllergyRecord } from "../db/db.types";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../utils/network";
import { reportAllergyCheckUnavailable } from "../utils/flow-telemetry";

// ─── Icons ────────────────────────────────────────────────────────────────────

const IcShield = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const IcPlus = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IcPencil = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IcTrash = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const IcWarning = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IcCheck = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Severity config ──────────────────────────────────────────────────────────

const SEVERITY_BADGE: Record<AllergySeverity, string> = {
  "Life-Threatening": "text-red-700 bg-red-100 border-red-300",
  "Severe":           "text-orange-700 bg-orange-100 border-orange-200",
  "Moderate":         "text-amber-700 bg-amber-50 border-amber-200",
  "Mild":             "text-yellow-700 bg-yellow-50 border-yellow-200",
};

const REACTION_TYPE_LABEL: Record<AllergyReactionType, string> = {
  Drug:          "Drug",
  Food:          "Food",
  Environmental: "Environmental",
  Contact:       "Contact",
};

const SEVERITIES: AllergySeverity[] = ["Mild", "Moderate", "Severe", "Life-Threatening"];
const REACTION_TYPES: AllergyReactionType[] = ["Drug", "Food", "Environmental", "Contact"];

// ─── Allergen master list ─────────────────────────────────────────────────────

const ALLERGEN_MASTER: Record<AllergyReactionType, string[]> = {
  Drug: [
    "Penicillin", "Amoxicillin", "Ampicillin", "Cloxacillin",
    "Sulfonamides", "Trimethoprim-Sulfamethoxazole",
    "Aspirin", "Ibuprofen", "Naproxen", "Diclofenac", "Celecoxib",
    "Codeine", "Morphine", "Tramadol",
    "Cephalosporins", "Ceftriaxone", "Cefixime", "Cephalexin",
    "Tetracycline", "Doxycycline",
    "Ciprofloxacin", "Levofloxacin", "Ofloxacin",
    "Azithromycin", "Erythromycin", "Clarithromycin",
    "Metronidazole", "Clindamycin", "Vancomycin",
    "Carbamazepine", "Phenytoin", "Phenobarbital", "Lamotrigine",
    "Allopurinol",
    "Enalapril", "Lisinopril", "Ramipril",
    "Atenolol", "Metoprolol", "Propranolol",
    "Atorvastatin", "Rosuvastatin", "Simvastatin",
    "Insulin", "Metformin", "Glibenclamide",
    "Contrast Dye (Iodine)", "Latex",
    "Heparin", "Warfarin",
    "Lidocaine", "Procaine", "Benzocaine",
    "Succinylcholine", "Atracurium",
    "Omeprazole", "Pantoprazole",
    "Diphenhydramine", "Cetirizine",
    "Prednisone", "Dexamethasone",
  ],
  Food: [
    "Peanuts", "Tree Nuts", "Almonds", "Cashews", "Walnuts", "Pistachios",
    "Milk", "Lactose", "Casein", "Whey",
    "Eggs",
    "Wheat", "Gluten",
    "Soy", "Soya",
    "Fish", "Shellfish", "Shrimp", "Crab", "Lobster", "Prawns",
    "Sesame", "Mustard",
    "Mango", "Banana", "Kiwi", "Papaya", "Avocado",
    "Tomatoes", "Brinjal (Eggplant)",
    "Maize (Corn)", "Rice",
    "Chocolate", "Cocoa",
    "Sulfites/Sulphites", "Benzoates", "Tartrazine (Yellow 5)",
    "MSG (Monosodium Glutamate)",
  ],
  Environmental: [
    "Dust Mites", "House Dust",
    "Pollen", "Tree Pollen", "Grass Pollen", "Weed Pollen",
    "Pet Dander", "Cat Dander", "Dog Dander",
    "Cockroach",
    "Mold", "Aspergillus", "Alternaria", "Cladosporium",
    "Bee Sting", "Wasp Sting", "Insect Stings",
    "Cold Air", "Exercise-Induced",
    "Smoke", "Cigarette Smoke",
    "Perfume", "Fragrance",
    "Chlorine", "Formaldehyde",
  ],
  Contact: [
    "Nickel", "Cobalt", "Chromium",
    "Latex/Rubber", "Natural Rubber Latex",
    "Cosmetics", "Hair Dye", "Paraphenylenediamine (PPD)",
    "Fragrances", "Preservatives",
    "Adhesives", "Epoxy Resin", "Acrylics",
    "Detergents", "Soaps", "Cleaning Products",
    "Poison Ivy", "Parthenium", "Tulsi",
    "Sunscreen", "Neomycin",
    "Nickel", "Gold", "Silver",
  ],
};

// ─── Empty form state ─────────────────────────────────────────────────────────

const EMPTY_FORM: AllergyCreate = {
  allergen: "",
  severity: "Mild",
  reaction_type: undefined,
  reaction: "",
  notes: "",
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface AllergyRowProps {
  allergy: AllergyResponse;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  editable: boolean;
}

const AllergyRow: React.FC<AllergyRowProps> = ({ allergy, onEdit, onDelete, isDeleting, editable }) => (
  <div
    className={`flex items-start gap-3 py-3 ${allergy.is_life_threatening ? "border-l-2 border-red-400 pl-3 -ml-3" : ""}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-slate-800 leading-tight">{allergy.allergen}</p>
        <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 leading-none ${SEVERITY_BADGE[allergy.severity]}`}>
          {allergy.severity}
        </span>
        {allergy.reaction_type && (
          <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 leading-none">
            {REACTION_TYPE_LABEL[allergy.reaction_type]}
          </span>
        )}
      </div>
      {allergy.reaction && (
        <p className="text-xs text-slate-500 mt-0.5 leading-snug">{allergy.reaction}</p>
      )}
    </div>

    {editable && (
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          title="Edit"
        >
          <IcPencil />
        </button>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
          title="Delete"
        >
          <IcTrash />
        </button>
      </div>
    )}
  </div>
);

// ─── Inline form ──────────────────────────────────────────────────────────────

interface AllergyFormProps {
  initial: AllergyCreate;
  onSave: (data: AllergyCreate) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}

const AllergyForm: React.FC<AllergyFormProps> = ({ initial, onSave, onCancel, saving, error }) => {
  const [form, setForm] = useState<AllergyCreate>(initial);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const comboRef = useRef<HTMLDivElement>(null);

  const set = <K extends keyof AllergyCreate>(key: K) =>
    (val: AllergyCreate[K]) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.allergen.trim()) return;
    await onSave(form);
  };

  const suggestions = useMemo(() => {
    const q = form.allergen.toLowerCase().trim();
    if (!q) return [];
    const out: { label: string; cat: AllergyReactionType }[] = [];
    for (const cat of Object.keys(ALLERGEN_MASTER) as AllergyReactionType[]) {
      for (const label of ALLERGEN_MASTER[cat]) {
        if (label.toLowerCase().includes(q)) out.push({ label, cat });
        if (out.length >= 8) break;
      }
      if (out.length >= 8) break;
    }
    return out;
  }, [form.allergen]);

  useEffect(() => {
    if (!showSuggestions) return;
    const handler = (e: MouseEvent) => {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSuggestions]);

  const fieldBase =
    "w-full text-base sm:text-sm text-slate-700 bg-slate-100 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-300 transition-shadow placeholder:text-slate-400";
  const selectBase = fieldBase + " cursor-pointer";

  return (
    <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-2.5 bg-slate-50 rounded-2xl border border-slate-100 p-3">
      {/* Allergen combobox */}
      <div ref={comboRef} className="relative">
        <input
          type="text"
          placeholder="Allergen name (type to search) *"
          value={form.allergen}
          onChange={e => { set("allergen")(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          required
          className={fieldBase}
          autoFocus
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
            {suggestions.map(({ label, cat }) => (
              <button
                key={`${cat}-${label}`}
                type="button"
                onMouseDown={() => {
                  set("allergen")(label);
                  if (!form.reaction_type) set("reaction_type")(cat);
                  setShowSuggestions(false);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center justify-between gap-2 border-b border-slate-50 last:border-0"
              >
                <span className="text-slate-700">{label}</span>
                <span className="text-[10px] text-slate-400 shrink-0 bg-slate-100 rounded-full px-1.5 py-0.5">{cat}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {/* Severity */}
        <select
          value={form.severity}
          onChange={e => set("severity")(e.target.value as AllergySeverity)}
          className={selectBase + " flex-1"}
        >
          {SEVERITIES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {/* Reaction type */}
        <select
          value={form.reaction_type ?? ""}
          onChange={e =>
            set("reaction_type")(e.target.value ? (e.target.value as AllergyReactionType) : undefined)
          }
          className={selectBase + " flex-1"}
        >
          <option value="">Type (opt.)</option>
          {REACTION_TYPES.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Reaction description */}
      <input
        type="text"
        placeholder="Reaction description (optional)"
        value={form.reaction ?? ""}
        onChange={e => set("reaction")(e.target.value || undefined)}
        className={fieldBase}
      />

      {/* Notes */}
      <input
        type="text"
        placeholder="Notes (optional)"
        value={form.notes ?? ""}
        onChange={e => set("notes")(e.target.value || undefined)}
        className={fieldBase}
      />

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !form.allergen.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
        >
          {saving ? (
            <>Saving…</>
          ) : (
            <><IcCheck />Save</>
          )}
        </button>
      </div>
    </form>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

interface AllergiesPanelProps {
  patientId: string;
  /** When false, the Add/Edit/Delete controls are hidden (read-only mode). */
  editable?: boolean;
  /**
   * True where this list is what the prescriber checks drugs against (the
   * Diagnosis Pad). A load that leaves it untrustworthy is then reported as
   * ALLERGY_CHECK_UNAVAILABLE; elsewhere it is only a display failure.
   */
  guardsPrescription?: boolean;
}

const AllergiesPanel: React.FC<AllergiesPanelProps> = ({
  patientId,
  editable = true,
  guardsPrescription = false,
}) => {
  const [allergies, setAllergies] = useState<AllergyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const lifeThreateningCount = allergies.filter(a => a.is_life_threatening).length;

  const load = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    try {
      const data = await AllergyAPI.list(patientId);
      setAllergies(data);
      setFetchError(null);
    } catch (e) {
      if (e instanceof AllergyApiError && e.statusCode === 404) {
        setAllergies([]);
        setFetchError(null);
      } else if (isPermissionDenied(e)) {
        // Refused, not offline. The IndexedDB fallback below takes ANY other
        // error, so without this a 403 rendered the very allergy list the grant
        // was just revoked for.
        setAllergies([]);
        setFetchError(actionErrorMessage(e, "Failed to load allergies"));
        if (guardsPrescription) reportAllergyCheckUnavailable("allergy list refused", e);
      } else {
        // Offline fallback: read from IndexedDB
        try {
          const rawRows = await db.allergies.where("patient_id").equals(patientId).toArray();
          if (rawRows.length === 0) {
            // No cached allergies for this patient — treat as empty, not error.
            // Offline that is the offline-first path working. Online, the
            // server failed and the drugs are being checked against nothing.
            if (guardsPrescription && navigator.onLine) {
              reportAllergyCheckUnavailable("server list failed, no local copy", e);
            }
            setAllergies([]);
            setFetchError(null);
            return;
          }
          if (rawRows.length > 0) {
            const decrypted = await Promise.all(
              rawRows.map(r => unwrapFromStorage<AllergyRecord>(r as unknown as Record<string, unknown>)),
            );
            setAllergies(decrypted.map(r => ({
              allergy_id:          r.allergy_id,
              patient_id:          r.patient_id,
              allergen:            r.allergen,
              severity:            r.severity,
              reaction_type:       r.reaction_type ?? null,
              reaction:            r.reaction ?? null,
              verified_date:       r.verified_date ?? null,
              notes:               r.notes ?? null,
              created_at:          r.created_at ?? new Date().toISOString(),
              is_life_threatening: r.severity === "Life-Threatening",
            })));
            setFetchError(null);
            return;
          }
        } catch (cacheErr) {
          // fall through to set error
          if (guardsPrescription) reportAllergyCheckUnavailable("local allergy cache unreadable", cacheErr);
        }
        setFetchError(
          actionErrorMessage(
            e, e instanceof AllergyApiError ? e.message : "Failed to load allergies",
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [patientId, guardsPrescription]);

  useEffect(() => {
    load();
  }, [load]);

  // Leaving edit mode (page-level Cancel/Save) — close any open add/edit form.
  useEffect(() => {
    if (!editable) {
      setShowAddForm(false);
      setEditingId(null);
      setFormError(null);
    }
  }, [editable]);

  const saveAllergyOffline = async (allergyId: string, data: AllergyCreate, op: "create" | "update") => {
    await putAndQueue<AllergyRecord>(
      "allergy",
      allergyId,
      {
        allergy_id:    allergyId,
        patient_id:    patientId,
        allergen:      data.allergen,
        severity:      data.severity,
        reaction_type: data.reaction_type ?? null,
        reaction:      data.reaction ?? null,
        verified_date: data.verified_date ?? null,
        notes:         data.notes ?? null,
      },
      op,
    );
  };

  const handleAdd = async (data: AllergyCreate) => {
    setFormSaving(true);
    setFormError(null);
    const newAllergyId = crypto.randomUUID();
    const optimistic: AllergyResponse = {
      allergy_id:          newAllergyId,
      patient_id:          patientId,
      allergen:            data.allergen,
      severity:            data.severity,
      reaction_type:       data.reaction_type ?? null,
      reaction:            data.reaction ?? null,
      verified_date:       data.verified_date ?? null,
      notes:               data.notes ?? null,
      created_at:          new Date().toISOString(),
      is_life_threatening: data.severity === "Life-Threatening",
    };

    if (!navigator.onLine) {
      try {
        await saveAllergyOffline(newAllergyId, data, "create");
        setAllergies(prev => [optimistic, ...prev]);
        setShowAddForm(false);
      } catch { setFormError("Failed to save offline"); }
      setFormSaving(false);
      return;
    }

    try {
      const created = await AllergyAPI.create(patientId, data);
      setAllergies(prev => [created, ...prev]);
      setShowAddForm(false);
    } catch (e) {
      // A refused write must surface the refusal, never be queued as offline.
      if (!isPermissionDenied(e) && isBackendUnreachable(e)) {
        try {
          await saveAllergyOffline(newAllergyId, data, "create");
          setAllergies(prev => [optimistic, ...prev]);
          setShowAddForm(false);
        } catch { setFormError("Failed to save offline"); }
        return;
      }
      setFormError(
        actionErrorMessage(e, e instanceof AllergyApiError ? e.message : "Failed to save"),
      );
    } finally {
      setFormSaving(false);
    }
  };

  const handleEdit = async (allergyId: string, data: AllergyCreate) => {
    setFormSaving(true);
    setFormError(null);

    if (!navigator.onLine) {
      try {
        await saveAllergyOffline(allergyId, data, "update");
        setAllergies(prev => prev.map(a => a.allergy_id === allergyId ? {
          ...a, ...data,
          is_life_threatening: data.severity === "Life-Threatening",
        } : a));
        setEditingId(null);
      } catch { setFormError("Failed to save offline"); }
      setFormSaving(false);
      return;
    }

    try {
      const updated = await AllergyAPI.update(patientId, allergyId, data);
      setAllergies(prev => prev.map(a => a.allergy_id === allergyId ? updated : a));
      setEditingId(null);
    } catch (e) {
      if (!isPermissionDenied(e) && isBackendUnreachable(e)) {
        try {
          await saveAllergyOffline(allergyId, data, "update");
          setAllergies(prev => prev.map(a => a.allergy_id === allergyId ? {
            ...a, ...data,
            is_life_threatening: data.severity === "Life-Threatening",
          } : a));
          setEditingId(null);
        } catch { setFormError("Failed to save offline"); }
        return;
      }
      setFormError(
        actionErrorMessage(e, e instanceof AllergyApiError ? e.message : "Failed to save"),
      );
    } finally {
      setFormSaving(false);
    }
  };

  // Offline delete: removes the local Dexie row and either cancels the pending
  // create (if this allergy was never synced) or queues a delete op for the
  // push engine to send when connectivity returns.
  const deleteAllergyOffline = async (allergyId: string) => {
    const localRow = await db.allergies.get(allergyId);
    const isPending = (localRow as any)?.sync_status === "pending";
    await db.allergies.delete(allergyId);
    if (isPending) {
      // Never reached the server — purge the pending create from the queue too.
      await db.sync_queue
        .where("[entity_type+entity_id]")
        .equals(["allergy", allergyId])
        .delete();
    } else {
      await enqueue("delete", "allergy", allergyId, { patient_id: patientId });
    }
  };

  const handleDelete = async (allergyId: string) => {
    setDeletingId(allergyId);
    try {
      if (!navigator.onLine) {
        await deleteAllergyOffline(allergyId);
        setAllergies(prev => prev.filter(a => a.allergy_id !== allergyId));
        return;
      }
      try {
        await AllergyAPI.delete(patientId, allergyId);
        await db.allergies.delete(allergyId);
        setAllergies(prev => prev.filter(a => a.allergy_id !== allergyId));
      } catch (e) {
        if (!isPermissionDenied(e) && isBackendUnreachable(e)) {
          await deleteAllergyOffline(allergyId);
          setAllergies(prev => prev.filter(a => a.allergy_id !== allergyId));
        } else {
          setFormError(actionErrorMessage(e, "Failed to delete allergy"));
        }
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 pt-4 pb-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center text-red-500">
            <IcShield />
          </div>
          <h2 className="text-[15px] font-bold text-slate-900">Allergies</h2>
          {allergies.length > 0 && (
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 leading-none">
              {allergies.length}
            </span>
          )}
        </div>
        {editable && !showAddForm && !editingId && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity bg-gradient-to-br from-blue-500 to-sky-400"
          >
            <IcPlus /> Add
          </button>
        )}
      </div>

      {/* Life-threatening banner */}
      {lifeThreateningCount > 0 && (
        <div className="flex items-center gap-2 bg-red-600 text-white rounded-xl px-3 py-2.5 mb-3 text-xs font-semibold">
          <IcWarning />
          <span>
            {lifeThreateningCount === 1
              ? "1 Life-Threatening Allergy on Record"
              : `${lifeThreateningCount} Life-Threatening Allergies on Record`}
          </span>
        </div>
      )}

      {/* Loading / error states */}
      {loading && (
        <p className="text-xs text-slate-400 text-center py-4">Loading…</p>
      )}
      {!loading && fetchError && (
        <p className="text-xs text-red-500 text-center py-4">{fetchError}</p>
      )}

      {/* Allergy list */}
      {!loading && !fetchError && allergies.length === 0 && !showAddForm && (
        <p className="text-xs text-slate-400 text-center py-4">No allergies recorded</p>
      )}

      {!loading && !fetchError && allergies.map((allergy, i) => (
        <div key={allergy.allergy_id}>
          {editingId === allergy.allergy_id ? (
            <div className="py-2">
              <AllergyForm
                initial={{
                  allergen: allergy.allergen,
                  severity: allergy.severity,
                  reaction_type: allergy.reaction_type ?? undefined,
                  reaction: allergy.reaction ?? "",
                  notes: allergy.notes ?? "",
                }}
                onSave={data => handleEdit(allergy.allergy_id, data)}
                onCancel={() => { setEditingId(null); setFormError(null); }}
                saving={formSaving}
                error={formError}
              />
            </div>
          ) : (
            <div className={i < allergies.length - 1 || showAddForm ? "border-b border-slate-100" : ""}>
              <AllergyRow
                allergy={allergy}
                onEdit={() => { setShowAddForm(false); setFormError(null); setEditingId(allergy.allergy_id); }}
                onDelete={() => handleDelete(allergy.allergy_id)}
                isDeleting={deletingId === allergy.allergy_id}
                editable={editable}
              />
            </div>
          )}
        </div>
      ))}

      {/* Add form */}
      {showAddForm && (
        <AllergyForm
          initial={EMPTY_FORM}
          onSave={handleAdd}
          onCancel={() => { setShowAddForm(false); setFormError(null); }}
          saving={formSaving}
          error={formError}
        />
      )}

    </div>
  );
};

export default AllergiesPanel;
